import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "../lib/api";
import VerticalTag from "../components/VerticalTag";
import WavyEnd from "../components/WavyEnd";
import PlotReveal from "../components/PlotReveal";

const FALLBACK = "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?fm=jpg&q=85&w=1200";

/**
 * A quiet notes / press page.
 *
 * Two sections:
 *   1. Press — aggregated from every published project's `featured_in` field,
 *      each entry linking back to the project so Google can index the trail.
 *   2. Studio Notes — a small stack of hand-written thoughts (semantic <article>
 *      per note) so the URL surfaces content beyond the project archive.
 */
export default function Journal() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const { data: projects = [] } = useQuery({
    queryKey: ["projects", "journal"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const press = useMemo(() => {
    const rows = [];
    for (const p of projects) {
      const list = Array.isArray(p.featured_in) ? p.featured_in : [];
      for (const entry of list) {
        if (!entry) continue;
        rows.push({ id: `${p.slug}-${entry}`, publication: entry, project: p });
      }
    }
    return rows;
  }, [projects]);

  const notes = [
    {
      id: "note-restraint",
      date: "February 2026",
      title: "Restraint is a form of care.",
      body: "The most generous thing a room can do is stay quiet. We keep coming back to this — not as a style, but as a discipline. Every surface earns its place, every joint is drawn twice, and the loudest thing in the room is the light.",
    },
    {
      id: "note-daylight",
      date: "December 2025",
      title: "Daylight is a material.",
      body: "A house designed for a north Sydney courtyard, on a mid-winter afternoon, taught us that daylight is measured in temperament, not lux. We now draw the sun before we draw the plan.",
    },
    {
      id: "note-slow-beginnings",
      date: "October 2025",
      title: "On slow beginnings.",
      body: "We accept a small number of commissions each year, and every one of them starts with two site visits before a line is drawn. The brief is always shorter afterwards.",
    },
  ];

  return (
    <div data-testid="journal-page" className="pt-28 md:pt-36">
      <VerticalTag text="Journal — Notes & Press" />

      {/* ---------- HERO ---------- */}
      <section className="container-w mb-24 md:mb-32 relative">
        <div className="mono-label mb-4">Journal</div>
        <h1 className="font-display italic text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-4xl">
          Quiet notes,<br />and where we&rsquo;ve<br />been mentioned.
        </h1>
        <div className="mt-8 max-w-xl">
          <p className="text-ash text-base md:text-lg leading-relaxed">
            A running log of the practice — short thoughts on how we work, and a record of the publications and juries who have written about our projects.
          </p>
          <WavyEnd className="text-marine" width={160} />
        </div>
      </section>

      {/* ---------- PRESS / FEATURED IN ---------- */}
      <section
        id="press"
        aria-labelledby="press-heading"
        className="container-w py-24 md:py-32 border-t border-ink/10"
        data-testid="press-section"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-3">
            <div className="mono-label mb-2">— 01</div>
            <h2 id="press-heading" className="font-display italic text-3xl md:text-5xl tracking-tight">
              Press
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-6">
            <p className="text-ash text-base md:text-lg leading-relaxed">
              Where our work has appeared — magazines, industry press and awards. Every entry links back to the project it references.
            </p>
            <WavyEnd className="text-marine" width={120} />
          </div>
        </div>

        {press.length === 0 ? (
          <p className="mono-label text-ash">No press entries yet — check back soon.</p>
        ) : (
          <ul className="border-t border-ink/10">
            {press.map((row, i) => {
              const img = row.project.gallery?.find((g) => g.id === row.project.hero_image_id) || row.project.gallery?.[0];
              const src = img ? fileUrl(img.variants.thumb || img.variants.grid) : FALLBACK;
              return (
                <li
                  key={row.id}
                  className="group border-b border-ink/10"
                  itemScope
                  itemType="https://schema.org/CreativeWork"
                  data-testid={`press-row-${i}`}
                >
                  <Link
                    to={`/work/${row.project.slug}`}
                    className="grid grid-cols-12 items-center gap-4 md:gap-8 py-6 md:py-8 transition-colors duration-500 hover:bg-bone/40"
                  >
                    <div className="col-span-2 md:col-span-1 mono-label text-ash">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-6 md:col-span-5">
                      <div className="font-display italic text-xl md:text-3xl tracking-tight" itemProp="publisher">
                        {row.publication}
                      </div>
                    </div>
                    <div className="hidden md:block md:col-span-3 mono-label text-ash">
                      <span itemProp="about">{row.project.title}</span>
                    </div>
                    <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-4">
                      <div className="hidden md:block overflow-hidden w-16 h-16">
                        <motion.img
                          src={src}
                          alt=""
                          aria-hidden
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.08 }}
                          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        />
                      </div>
                      <span className="mono-label link-underline text-marine">Project →</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ---------- STUDIO NOTES ---------- */}
      <section
        id="notes"
        aria-labelledby="notes-heading"
        className="container-w py-24 md:py-32 border-t border-ink/10"
        data-testid="notes-section"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-3">
            <div className="mono-label mb-2">— 02</div>
            <h2 id="notes-heading" className="font-display italic text-3xl md:text-5xl tracking-tight">
              Notes
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-6">
            <p className="text-ash text-base md:text-lg leading-relaxed">
              Short field notes — how we think, why we draw the way we do, what a slow practice means.
            </p>
            <WavyEnd className="text-marine" width={120} />
          </div>
        </div>

        <div className="space-y-16 md:space-y-24">
          {notes.map((n, i) => (
            <PlotReveal key={n.id} delay={i * 0.05}>
              <article
                id={n.id}
                itemScope
                itemType="https://schema.org/BlogPosting"
                className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-ink/10 pt-10"
                data-testid={`note-${i}`}
              >
                <div className="md:col-span-3">
                  <div className="mono-label text-ash" itemProp="datePublished">{n.date}</div>
                </div>
                <div className="md:col-span-8">
                  <h3
                    className="font-display italic text-2xl md:text-4xl leading-tight tracking-tight mb-6"
                    itemProp="headline"
                  >
                    {n.title}
                  </h3>
                  <p className="text-ash text-base md:text-lg leading-relaxed max-w-[62ch]" itemProp="articleBody">
                    {n.body}
                  </p>
                  <WavyEnd className="text-marine" width={140} />
                </div>
              </article>
            </PlotReveal>
          ))}
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="container-w py-24 md:py-32 border-t border-ink/10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <h2 className="font-display italic text-3xl md:text-5xl tracking-tight max-w-xl">
            Have a project in mind?
          </h2>
          <Link
            to="/contact"
            data-testid="journal-contact-cta"
            className="mono-label px-8 py-4 border border-ink/20 text-marine hover:bg-marine hover:text-paper transition-colors duration-500 self-start"
          >
            Start an enquiry →
          </Link>
        </div>
      </section>
    </div>
  );
}
